import type { FormEvent } from 'react';
import { Badge, Button, Col, Form, Row } from 'react-bootstrap';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../shared/CurrencyContext';
import NumberInput from '../../../components/form/NumberInput';
import PaginatedAsyncSelect from '../../../components/form/PaginatedAsyncSelect';
import { ProductDimensionsFields, ProductDimensionsInfo } from './ProductDimensions';

interface SelectOption {
  value: number | string;
  label: string;
}

const mapNamedOption = (entry: any): SelectOption => ({
  value: entry.id,
  label: entry.parent_name ? `${entry.parent_name} / ${entry.name}` : entry.name,
});

const mapCodeOrNameOption = (entry: any): SelectOption => ({
  value: entry.id,
  label: entry.name || entry.code || entry.symbol || `ID ${entry.id}`,
});

interface ProductInfoCardProps {
  product: any;
  form: any;
  setForm: (updater: (prev: any) => any) => void;
  editing: boolean;
  defaultTax: any;
  saving: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

const ProductInfoCard = ({ product, form, setForm, editing, defaultTax, saving, onSubmit, onCancel }: ProductInfoCardProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();

  const taxRatePercent = defaultTax?.rate ? parseFloat(defaultTax.rate) : 0;

  const handlePricePurchaseChange = (val: string) => {
    if (val === '') {
      setForm((prev: any) => ({ ...prev, price_purchase: '', price_purchase_vat: '' }));
      return;
    }
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && taxRatePercent > 0) {
      const vatVal = (parsed * (1 + taxRatePercent / 100)).toFixed(2);
      setForm((prev: any) => ({ ...prev, price_purchase: val, price_purchase_vat: vatVal }));
    } else {
      setForm((prev: any) => ({ ...prev, price_purchase: val }));
    }
  };

  const handlePricePurchaseVatChange = (val: string) => {
    if (val === '') {
      setForm((prev: any) => ({ ...prev, price_purchase_vat: '', price_purchase: '' }));
      return;
    }
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && taxRatePercent > 0) {
      const noVatVal = (parsed / (1 + taxRatePercent / 100)).toFixed(2);
      setForm((prev: any) => ({ ...prev, price_purchase_vat: val, price_purchase: noVatVal }));
    } else {
      setForm((prev: any) => ({ ...prev, price_purchase_vat: val }));
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  if (!editing) {
    return (
      <div className="datagrid">
        <div className="datagrid-item">
          <div className="datagrid-title">SKU</div>
          <div className="datagrid-content">{product.sku}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Kategorie</div>
          <div className="datagrid-content">{product.category_name || '-'}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Typ produktu</div>
          <div className="datagrid-content">{product.product_type_name || '-'}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Typ trasování</div>
          <div className="datagrid-content">
            <Badge bg="secondary">{product.tracking_type}</Badge>
          </div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Výchozí cena / den</div>
          <div className="datagrid-content">{formatCurrency(product.default_daily_rate_display || 0)}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Pořizovací cena (bez DPH)</div>
          <div className="datagrid-content">
            {product.price_purchase ? formatCurrency(product.price_purchase) : '-'}
          </div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Pořizovací cena (s DPH)</div>
          <div className="datagrid-content">
            {product.price_purchase_vat ? formatCurrency(product.price_purchase_vat) : '-'}
          </div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Hmotnost</div>
          <div className="datagrid-content">
            {product.weight ? `${product.weight} ${product.weight_unit_symbol || product.weight_unit_name || ''}` : '-'}
          </div>
        </div>
        <ProductDimensionsInfo product={product} />
        <div className="datagrid-item">
          <div className="datagrid-title">Vyloučit ze slevy</div>
          <div className="datagrid-content">
            {product.exclude_from_discount ? (
              <Badge bg="warning" text="dark">Ano</Badge>
            ) : (
              <Badge bg="outline-secondary">Ne</Badge>
            )}
          </div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Priorita</div>
          <div className="datagrid-content">{product.priority ?? 0}</div>
        </div>
        <div className="datagrid-item">
          <div className="datagrid-title">Barva</div>
          <div className="datagrid-content d-flex align-items-center gap-2">
            <span
              className="d-inline-block rounded-circle"
              style={{ width: '14px', height: '14px', backgroundColor: product.color || '#066fd1' }}
            />
            {product.color || '-'}
          </div>
        </div>

        {product.description && (
          <div className="datagrid-item w-100 mt-2">
            <div className="datagrid-title">Popis</div>
            <div className="datagrid-content text-wrap">{product.description}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      {/* 1. ZÁKLADNÍ ÚDAJE */}
      <h6 className="text-muted mb-3">{t('warehouses.products.basicInfo')}</h6>
      <Row className="g-3 mb-3">
        <Col md={8}>
          <Form.Group controlId="product-name">
            <Form.Label>{t('warehouses.products.name')} *</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="product-sku">
            <Form.Label>SKU *</Form.Label>
            <Form.Control
              value={form.sku}
              onChange={(e) => setForm(prev => ({ ...prev, sku: e.target.value }))}
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Form.Group controlId="product-category">
            <Form.Label>{t('warehouses.categories.title')}</Form.Label>
            <PaginatedAsyncSelect
              endpoint="warehouses/api/product-categories/"
              value={form.category_option}
              onChange={(opt: SelectOption | null) =>
                setForm(prev => ({ ...prev, category: opt?.value || '', category_option: opt }))
              }
              mapOption={mapNamedOption}
              placeholder={t('warehouses.products.selectCategory')}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="product-type">
            <Form.Label>{t('warehouses.products.productType')}</Form.Label>
            <PaginatedAsyncSelect
              endpoint="warehouses/api/product-types/"
              value={form.product_type_option}
              onChange={(opt: SelectOption | null) =>
                setForm(prev => ({ ...prev, product_type: opt?.value || '', product_type_option: opt }))
              }
              mapOption={mapNamedOption}
              placeholder={t('warehouses.products.selectProductType')}
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group controlId="product-description" className="mb-3">
        <Form.Label>{t('warehouses.items.description')}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={form.description}
          onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
        />
      </Form.Group>

      <hr className="my-4" />

      {/* 2. KLASIFIKACE & VZHLED */}
      <h6 className="text-muted mb-3">{t('warehouses.products.settingsAndClassification')}</h6>
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Form.Group controlId="product-tracking-type">
            <Form.Label>{t('warehouses.products.trackingType')}</Form.Label>
            <Form.Select
              value={form.tracking_type}
              onChange={(e) => setForm(prev => ({ ...prev, tracking_type: e.target.value }))}
            >
              <option value="SERIALIZED">{t('warehouses.products.trackingSerialized')}</option>
              <option value="BULK">{t('warehouses.products.trackingBulk')}</option>
              <option value="CONSUMABLE">{t('warehouses.products.trackingConsumable')}</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="product-priority">
            <Form.Label>{t('warehouses.products.priority')}</Form.Label>
            <NumberInput
              step="any"
              value={form.priority}
              onChange={(priority) => setForm(prev => ({ ...prev, priority }))}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="product-color">
            <Form.Label>{t('warehouses.products.color')}</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="color"
                value={/^#[0-9A-F]{6}$/i.test(form.color) ? form.color : '#000000'}
                onChange={(e) => setForm(prev => ({ ...prev, color: e.target.value }))}
                style={{ width: '50px', height: '38px', padding: '2px' }}
              />
              <Form.Control
                type="text"
                value={form.color}
                onChange={(e) => setForm(prev => ({ ...prev, color: e.target.value }))}
              />
            </div>
          </Form.Group>
        </Col>
      </Row>

      <hr className="my-4" />

      {/* 3. FYZICKÉ VLASTNOSTI */}
      <h6 className="text-muted mb-3">{t('warehouses.products.physicalProperties')}</h6>
      <Row className="g-3 mb-3">
        <Col md={6}>
          <Form.Group controlId="product-weight">
            <Form.Label>{t('warehouses.products.weight')}</Form.Label>
            <Form.Control
              type="number"
              step="any"
              min="0"
              value={form.weight}
              onChange={(e) => setForm(prev => ({ ...prev, weight: e.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="product-weight-unit">
            <Form.Label>{t('warehouses.products.weightUnit')}</Form.Label>
            <PaginatedAsyncSelect
              endpoint="settings/api/weight-units/"
              value={form.weight_unit_option}
              onChange={(opt: SelectOption | null) =>
                setForm(prev => ({ ...prev, weight_unit: opt?.value || '', weight_unit_option: opt }))
              }
              mapOption={mapCodeOrNameOption}
              placeholder={t('warehouses.products.selectUnit')}
            />
          </Form.Group>
        </Col>
      </Row>
      <ProductDimensionsFields value={form} onChange={(patch) => setForm((prev: any) => ({ ...prev, ...patch }))} />

      <hr className="my-4" />

      {/* 4. FINANCE & TARIF */}
      <h6 className="text-muted mb-3">{t('warehouses.products.financeAndTariff')}</h6>
      <Row className="g-3 mb-3">
        <Col md={6}>
          <Form.Group controlId="product-default-rate">
            <Form.Label>{t('warehouses.products.defaultDailyRate')} *</Form.Label>
            <Form.Control
              type="number"
              step="any"
              min="0"
              value={form.default_daily_rate}
              onChange={(e) => setForm(prev => ({ ...prev, default_daily_rate: e.target.value }))}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="product-currency">
            <Form.Label>{t('warehouses.items.currency')}</Form.Label>
            <PaginatedAsyncSelect
              endpoint="settings/api/currency/"
              value={form.currency_option}
              onChange={(opt: SelectOption | null) =>
                setForm(prev => ({ ...prev, currency: opt?.value || '', currency_option: opt }))
              }
              mapOption={mapCodeOrNameOption}
              placeholder={t('warehouses.products.defaultCurrency')}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={4}>
          <Form.Group controlId="product-price-purchase">
            <Form.Label>{t('warehouses.products.purchasePrice')}</Form.Label>
            <Form.Control
              type="number"
              step="any"
              min="0"
              placeholder="0.00"
              value={form.price_purchase}
              onChange={(e) => handlePricePurchaseChange(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="product-price-purchase-vat">
            <Form.Label>
              {t('warehouses.products.purchasePriceVat')}
              {defaultTax?.rate && <small className="text-muted ms-1">({parseFloat(defaultTax.rate)}%)</small>}
            </Form.Label>
            <Form.Control
              type="number"
              step="any"
              min="0"
              placeholder="0.00"
              value={form.price_purchase_vat}
              onChange={(e) => handlePricePurchaseVatChange(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-center mt-4">
          <Form.Group controlId="exclude_from_discount">
            <Form.Check
              type="checkbox"
              label={t('warehouses.products.excludeFromDiscount')}
              checked={form.exclude_from_discount}
              onChange={(e) => setForm(prev => ({ ...prev, exclude_from_discount: e.target.checked }))}
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2 mt-4">
        <Button type="submit" disabled={saving}>
          <IconDeviceFloppy size={16} className="me-1" />
          {t('warehouses.products.saveChanges')}
        </Button>
        <Button type="button" variant="outline-secondary" onClick={onCancel}>
          Zrušit
        </Button>
      </div>
    </Form>
  );
};

export default ProductInfoCard;
